"use client";

import { useState } from "react";
import { Reveal } from "@/components/ui/Reveal";
import { BeforeAfterSlider } from "@/components/home/BeforeAfterSlider";
import { CELEBRITY_TRANSPLANTS } from "@/lib/site-data";
import { motion, AnimatePresence } from "framer-motion";

export interface CelebrityTrustProps {
  eyebrow?: string;
  title?: string;
  highlightText?: string;
  description?: string;
  items?: Array<{
    name: string;
    title: string;
    beforeImage: string;
    afterImage: string;
  }>;
}

export function CelebrityTrust({
  eyebrow,
  title,
  highlightText,
  description,
  items,
}: CelebrityTrustProps) {
  const finalEyebrow = eyebrow || "Your Best Hair Transplant Center";
  const finalTitle = title || "Trusted by";
  const finalHighlightText = highlightText !== undefined ? highlightText : "Celebrities & Champions";
  const finalDescription = description || "Drag the slider on each photo to see the transformation for yourself.";
  const finalItems = items && items.length > 0 ? items : CELEBRITY_TRANSPLANTS;

  const [activeIndex, setActiveIndex] = useState(0);

  const prevCelebrity = () => {
    setActiveIndex((prev) => (prev === 0 ? finalItems.length - 1 : prev - 1));
  };

  const nextCelebrity = () => {
    setActiveIndex((prev) => (prev === finalItems.length - 1 ? 0 : prev + 1));
  };

  const activePerson = finalItems[activeIndex] || finalItems[0];

  return (
    <section className="bg-brand-gray-light py-12 sm:py-16 md:py-20">
      <div className="w-full px-4 sm:px-8 md:px-12 xl:px-[170px]">
        <Reveal className="max-w-4xl mx-auto text-center mb-6 sm:mb-10 md:mb-16">
          <span className="font-accent text-xs uppercase text-brand-orange-deep">
            {finalEyebrow}
          </span>
          <h2 className="mt-3 sm:mt-4 text-2xl sm:text-4xl md:text-5xl font-semibold text-brand-black">
            {finalTitle}{" "}
            {finalHighlightText && (
              <span className="text-brand-orange">{finalHighlightText}</span>
            )}
          </h2>
          <div
            className="mt-3 sm:mt-4 max-w-2xl mx-auto text-brand-gray text-xs sm:text-sm text-center [&>p]:mt-1 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: finalDescription }}
          />
        </Reveal>

        {/* Mobile View: Compact Interactive Showcase (< md) */}
        <div className="block md:hidden">
          {/* Celebrity Selection Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2.5 mb-5 scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-1 justify-start sm:justify-center">
            {finalItems.map((person, i) => (
              <button
                key={person.name}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                  i === activeIndex
                    ? "bg-brand-orange text-white shadow-md shadow-brand-orange/25 scale-[1.03]"
                    : "bg-white text-brand-black/75 border border-brand-gray/20 hover:border-brand-orange/40"
                }`}
              >
                {person.name}
              </button>
            ))}
          </div>

          {/* Active Celebrity Card (Proportionately sized, touch-friendly) */}
          <div className="max-w-[290px] sm:max-w-[320px] mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={activePerson.name}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="w-full flex justify-center"
              >
                <BeforeAfterSlider
                  name={activePerson.name}
                  title={activePerson.title}
                  beforeImage={activePerson.beforeImage}
                  afterImage={activePerson.afterImage}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Mobile Navigation Controls & Indicator */}
          <div className="flex items-center justify-between max-w-[290px] sm:max-w-[320px] mx-auto mt-4 px-2">
            <button
              type="button"
              onClick={prevCelebrity}
              aria-label="Previous celebrity"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-black shadow-sm border border-brand-gray/15 hover:bg-brand-orange hover:text-white transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Pagination dots & counter */}
            <div className="flex items-center gap-1.5">
              {finalItems.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Go to celebrity ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    i === activeIndex
                      ? "w-5 bg-brand-orange"
                      : "w-2 bg-brand-gray/30 hover:bg-brand-gray/50"
                  }`}
                />
              ))}
              <span className="ml-2 text-xs font-semibold text-brand-gray">
                {activeIndex + 1}/{finalItems.length}
              </span>
            </div>

            <button
              type="button"
              onClick={nextCelebrity}
              aria-label="Next celebrity"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-black shadow-sm border border-brand-gray/15 hover:bg-brand-orange hover:text-white transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <p className="text-center text-[11px] text-brand-gray/80 mt-2.5">
            Drag photo slider left &amp; right to compare results
          </p>
        </div>

        {/* Desktop View: Full 5-Column Grid (Preserved exactly as original) */}
        <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-5">
          {finalItems.map((person, i) => (
            <BeforeAfterSlider key={person.name} delay={i * 0.08} {...person} />
          ))}
        </div>
      </div>
    </section>
  );
}
